import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Paper, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  DoneAll as DoneAllIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { getAllNotifications, markAllNotificationsRead, markNotificationRead } from '../api';

const iconFor = (type = '') => {
  if (type.includes('delivery')) return <LocalShippingIcon />;
  if (type.includes('fabric') || type.includes('material')) return <InventoryIcon />;
  if (type.includes('task')) return <AssignmentIcon />;
  if (type.includes('warning') || type.includes('defect')) return <WarningIcon />;
  if (type.includes('success') || type.includes('completed')) return <CheckCircleIcon />;
  return <InfoIcon />;
};

const colorFor = (type = '') => {
  if (type.includes('warning') || type.includes('defect')) return { bg: '#fff7ed', fg: '#c2410c' };
  if (type.includes('success') || type.includes('completed') || type.includes('paid')) return { bg: '#ecfdf5', fg: '#047857' };
  return { bg: '#eff6ff', fg: '#1d4ed8' };
};

const formatTime = (value) => value
  ? new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '';

export default function Notifications() {
  const [tab, setTab] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestGeneration = useRef(0);
  const requestController = useRef(null);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setLoading(true);
    setError('');
    try {
      const response = await getAllNotifications({}, { signal: controller.signal });
      if (generation !== requestGeneration.current) return;
      setNotifications(response.data || []);
    } catch (requestError) {
      if (requestError.code === 'ERR_CANCELED' || generation !== requestGeneration.current) return;
      setError(requestError.response?.data?.detail || 'Не удалось загрузить уведомления');
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => requestController.current?.abort();
  }, [load]);

  const markRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((items) => items.map((item) => item.id === id ? { ...item, is_read: true } : item));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Не удалось обновить уведомление');
    }
  };

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((items) => items.map((item) => ({ ...item, is_read: true })));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Не удалось отметить уведомления');
    }
  };

  const unreadCount = notifications.filter((item) => !item.is_read).length;
  const visible = tab === 0 ? notifications : notifications.filter((item) => tab === 1 ? !item.is_read : item.is_read);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.7rem', sm: '2.125rem' } }}>Уведомления</Typography>
          <Typography color="text.secondary">{unreadCount ? `Непрочитанных: ${unreadCount}` : 'Все уведомления прочитаны'}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Обновить</Button>
          <Button variant="outlined" startIcon={<DoneAllIcon />} onClick={markAll} disabled={!unreadCount}>Прочитать все</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Повторить</Button>} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: { xs: 0, sm: 2 } }}>
          <Tab label={`Все (${notifications.length})`} />
          <Tab label={`Непрочитанные (${unreadCount})`} />
          <Tab label="Прочитанные" />
        </Tabs>
        <Divider />
        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 8, px: 2, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 56, color: 'grey.300', mb: 1 }} />
            <Typography variant="h6">Нет уведомлений</Typography>
            <Typography variant="body2" color="text.secondary">Новые события появятся здесь автоматически</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {visible.map((notification, index) => {
              const color = colorFor(notification.type);
              return (
                <React.Fragment key={notification.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{ px: { xs: 1.5, sm: 3 }, py: 2, pr: { xs: 6, sm: 8 }, bgcolor: notification.is_read ? 'transparent' : color.bg }}
                    secondaryAction={!notification.is_read ? (
                      <IconButton aria-label="Отметить прочитанным" onClick={() => markRead(notification.id)}><DoneAllIcon /></IconButton>
                    ) : null}
                  >
                    <ListItemIcon sx={{ minWidth: 52, mt: 0.25 }}>
                      <Avatar sx={{ bgcolor: color.bg, color: color.fg }}>{iconFor(notification.type)}</Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Stack direction="row" spacing={1} alignItems="center"><Typography fontWeight={notification.is_read ? 500 : 700}>{notification.title}</Typography>{!notification.is_read && <Chip label="Новое" size="small" color="primary" />}</Stack>}
                      secondary={<><Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>{notification.message}</Typography><Typography component="span" variant="caption" color="text.disabled">{formatTime(notification.created_at)}</Typography></>}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
}
